// React
import { Fragment, useMemo, useEffect, useCallback } from "react"

// Dates
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// Translations
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"
import { emptyValue } from "@/utils/helpers"

// GraphQL
import {
	PaymentEntryType,
	ProjectTypeEnum,
	ProjectAmortizationScheduleEnum,
	PayOutState,
	useAllInvestorPaymentsQuery,
} from "@/api/graphql"

// Tables
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
	CellContext,
	createColumnHelper,
} from "@/lib/table"
import {
	TableBody,
	TableDataCell,
	TableHead,
	TableRowCell,
	TableHeading,
	Table,
} from "@/components/table-controls/TableItems"

// UI
import { cn } from "@/lib/utils"
import { Heading } from "@/components/Typography"

// Icons
import { FiHash } from "@/lib/icons"

const columnHelper = createColumnHelper<PaymentEntryType>()

interface PayoutsTablePrintProps {
	enabled?: boolean
	onDataReady?: () => void
}

/**
 * PayoutsTablePrint
 * @returns
 */
export function PayoutsTablePrint({
	enabled = false,
	onDataReady,
}: PayoutsTablePrintProps) {
	// Translations
	const t = useTrans("project")
	const { formatCurrency, formatNumber } = useLang()

	// Queries
	const { data, isLoading } = useAllInvestorPaymentsQuery(
		{
			ordering: "-payment__interestperiod__end", // Uses internal python model structure
			limit: null, // This will return alls results
		},
		{
			enabled,
		},
	)

	// Memoize the data ready handler
	const handleDataReady = useCallback(() => {
		onDataReady?.()
	}, [onDataReady])

	// Notify parent when data has loaded successfully
	useEffect(() => {
		if (!isLoading && data?.me?.payment_entries?.results) {
			handleDataReady()
		}
	}, [isLoading, data?.me?.payment_entries?.results, handleDataReady])

	// Memo
	const investments = useMemo(() => {
		return (
			data?.me?.payment_entries?.results?.map((payment) => {
				return {
					...payment,
				}
			}) ?? []
		)
	}, [data?.me?.payment_entries?.results])

	const columns = useMemo(
		() => [
			columnHelper.accessor(
				(data: PaymentEntryType) => data?.project?.name,
				{
					id: "project:name",
					header: () => (
						<TableHeading variant="static">
							{t("project:revenue.table.headers.project")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, string>) => (
						<TableDataCell className="font-medium" multiline>
							{info.getValue()}
						</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) => data?.transaction_id,
				{
					id: "transaction_id",
					header: () => (
						<TableHeading variant="static">
							{t("project:revenue.table.headers.transaction_id")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, string>) => (
						<TableDataCell
							fieldName={t(
								"project:revenue.table.headers.transaction_id",
							)}
						>
							<span className="flex flex-row">
								<FiHash
									className="mr-1.5 size-5 shrink-0 text-gray-400"
									aria-hidden="true"
								/>
								{info.getValue()}
							</span>
						</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					DateTime.fromISO(data.interest_period?.start).toFormat(
						dateFormat,
					),
				{
					id: "interest_period.start",
					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.interest_period")}
						</TableHeading>
					),
					cell: (info) => (
						<TableDataCell>{info.getValue()}</TableDataCell>
					),
				},
			),
			columnHelper.accessor((data: PaymentEntryType) => data, {
				id: "payment_deadline_date",
				header: () => (
					<TableHeading>
						{t(
							"project:revenue.table.headers.payment_deadline_date",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<PaymentEntryType, PaymentEntryType>,
				) => {
					return (
						<TableDataCell>
							{DateTime.fromISO(
								info.getValue()?.interest_period
									?.payment_deadline_date,
							).toFormat(dateFormat) || emptyValue()}
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor((data: PaymentEntryType) => data, {
				id: "payment_completion_date",
				header: () => (
					<TableHeading>
						{t(
							"project:revenue.table.headers.payment_completion_date",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<PaymentEntryType, PaymentEntryType>,
				) => {
					return (
						<TableDataCell
							fieldName={t(
								"project:revenue.table.headers.payment_deadline_date",
							)}
						>
							<>
								{/** Show date completed */}
								{info.getValue().payment_completion_date ? (
									<>
										{DateTime.fromISO(
											info.getValue()
												?.payment_completion_date,
										).toFormat(dateFormat)}
									</>
								) : (
									<div className="flex items-center">
										{emptyValue()}
									</div>
								)}
							</>
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor((data: PaymentEntryType) => data, {
				id: "payment_entry.state",
				header: () => (
					<TableHeading>
						{t("project:revenue.table.headers.payout_state")}
					</TableHeading>
				),
				cell: (
					info: CellContext<PaymentEntryType, PaymentEntryType>,
				) => (
					<TableDataCell multiline>
						<span
							className={cn(
								info.getValue().state ===
									PayOutState.PayoutCompleted &&
									"text-green-500",
								info.getValue().state !==
									PayOutState.PayoutCompleted &&
									info.getValue().state !== null &&
									DateTime.fromISO(
										info.getValue().interest_period
											?.payment_deadline_date,
									) < DateTime.now() &&
									"text-red-500",
							)}
						>
							{info.getValue().state
								? t(
										`project:revenue.payment_entry_states.${info.getValue().state}`,
									)
								: t(
										`project:revenue.payment_entry_states.unknown`,
									)}
						</span>
					</TableDataCell>
				),
			}),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					data?.share_count
						? formatNumber(data.share_count)
						: emptyValue(),
				{
					id: "share_count",
					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.solar_shares")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, string>) => (
						<TableDataCell>{info.getValue()}</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					data?.cost ? formatCurrency(data.cost) : emptyValue(),
				{
					id: "cost",
					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.interest")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, string>) => (
						<TableDataCell>{info.getValue()}</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					data?.amortization
						? formatCurrency(data.amortization)
						: emptyValue(),
				{
					id: "amortization",
					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.amortization")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, string>) => (
						<TableDataCell>{info.getValue()}</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					data?.repayment
						? formatCurrency(data.repayment)
						: emptyValue(),
				{
					id: "repayment",

					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.compensation")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, string>) => (
						<TableDataCell>{info.getValue()}</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					Number(data?.cost) +
					Number(data?.amortization) +
					Number(data?.repayment),
				{
					id: "paymentSum",
					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.payment")}
						</TableHeading>
					),
					cell: (info: CellContext<PaymentEntryType, number>) => (
						<TableDataCell>
							{info.getValue()
								? formatCurrency(info.getValue())
								: emptyValue()}
						</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) => data?.project?.type,
				{
					id: "project:type",
					header: () => (
						<TableHeading>
							{t("project:revenue.table.headers.project_type")}
						</TableHeading>
					),
					cell: (
						info: CellContext<PaymentEntryType, ProjectTypeEnum>,
					) => (
						<TableDataCell>
							{t(`common:project.type.${info.getValue()}`)}
						</TableDataCell>
					),
				},
			),
			columnHelper.accessor(
				(data: PaymentEntryType) =>
					data?.project?.amortization_schedule,
				{
					id: "project:amortization_schedule",
					header: () => (
						<TableHeading>
							{t(
								"project:revenue.table.headers.project_amortization_schedule",
							)}
						</TableHeading>
					),
					cell: (
						info: CellContext<
							PaymentEntryType,
							ProjectAmortizationScheduleEnum
						>,
					) => (
						<TableDataCell multiline>
							{t(
								`common:project.amortization_schedule.${info.getValue()}`,
							)}
						</TableDataCell>
					),
				},
			),
		],
		[t, formatCurrency, formatNumber],
	)
	const tableData = useMemo(
		() => investments as PaymentEntryType[],
		[investments],
	)
	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	})

	// No data state
	if (!isLoading && (!investments || investments.length === 0)) {
		return (
			<>
				<div
					className={
						"grid-rows-[minmax(20px,1fr)_1fr] rounded-md bg-gray-50 px-4 py-3"
					}
				>
					<Heading as="h2" styleAs="h3">
						{t("project:revenue.all.title")}
					</Heading>
				</div>
				<div className="py-8 text-center text-gray-500">
					{t("project:revenue.no-data.copy")}
				</div>
			</>
		)
	}

	// Data state
	return (
		<>
			<div
				className={
					"grid-rows-[minmax(20px,1fr)_1fr] rounded-md bg-gray-50 px-4 py-3"
				}
			>
				<Heading as="h2" styleAs="h3">
					{t("project:revenue.all.title")}
				</Heading>
			</div>
			<Table
				className="w-full"
				isLoading={isLoading}
				headerGroups={table.getHeaderGroups()}
			>
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRowCell key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								)
							})}
						</TableRowCell>
					))}
				</TableHead>
				<TableBody>
					{table.getRowModel().rows.map((row) => {
						const isOdd = row.index % 2 === 0
						let className = ""

						return (
							<Fragment key={row.id}>
								<TableRowCell
									isOdd={isOdd}
									className={cn(className)}
								>
									{row.getAllCells().map((cell) => {
										return (
											<Fragment key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</Fragment>
										)
									})}
								</TableRowCell>
							</Fragment>
						)
					})}
				</TableBody>
			</Table>
		</>
	)
}
